// Business-facing marketing page
export const HOME_MARKUP = `<main id="main">
      <section class="hero wrap" aria-labelledby="hero-heading">
        <div class="hero-grid">
          <div>
            <h1 id="hero-heading">Identity verified at the door. One clean record in your system.</h1>
            <p class="hero-lead">
              IDGate gives any regulated business a QR-based Aadhaar check-in flow out of the box &mdash; guests verify through the Aadhaar app, fill your custom fields, and the backend delivers one merged record to your webhook or Google Sheet. No paper. No manual ID checks. No duplicate entries.
            </p>
            <div class="hero-ctas">
              <a class="btn btn-primary" href="https://app.idgate.in/signup" rel="noopener noreferrer">Start free pilot</a>
              <a class="btn btn-ghost" href="/contact">Contact us</a>
            </div>
          </div>
          <div class="hero-visual">
            <img src="/idgate-hero.png" alt="No paper. No photocopy. Just one clean record — QR scan to verified guest record." class="hero-image" />
          </div>
        </div>

      </section>

      <section class="section alt" id="how" aria-labelledby="how-heading">
        <div class="wrap">
          <div class="section-head">
            <h2 id="how-heading">From QR scan to verified record in three steps</h2>
            <p class="muted" style="margin-top:0.5rem">Identity is always verified first &mdash; custom fields follow. No exceptions.</p>
          </div>
          <div class="how-visual">
            <img src="/Story%20Arc.png" alt="How it works: Entrance kiosk QR scan → UIDAI verification with custom fields → User-owned storage (Cloud / Sheet)" class="how-image" />
          </div>
          <div class="wizard">

            <div class="wizard-step">
              <div class="wizard-step-head">
                <div class="wizard-num" style="background: linear-gradient(135deg, #0f766e, #0d9488)">01</div>
              </div>
              <div class="wizard-card">
                <div class="wizard-icon" style="background: rgba(15,118,110,0.1); color: #0f766e">
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="24" height="24"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><path d="M14 15h3v3h-3zM17 18h3v3h-3z"/></svg>
                </div>
                <h3>Guest scans, Aadhaar opens</h3>
                <p>The operator publishes a QR code from the console. The guest scans it and verifies their identity through the Aadhaar app &mdash; the same one they already have.</p>
                <ul class="wizard-bullets">
                  <li>QR published instantly from your console</li>
                  <li>Works with any camera app</li>
                  <li>Aadhaar app opens automatically</li>
                </ul>
                <span class="wizard-tag">QR code &middot; Camera &middot; Aadhaar app</span>
              </div>
            </div>

            <div class="wizard-step">
              <div class="wizard-step-head">
                <div class="wizard-num" style="background: linear-gradient(135deg, #2563eb, #4f46e5)">02</div>
              </div>
              <div class="wizard-card">
                <div class="wizard-icon" style="background: rgba(79,70,229,0.1); color: #4f46e5">
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="24" height="24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>
                </div>
                <h3>Identity confirmed, form appears</h3>
                <p>Once UIDAI confirms the identity, your custom fields appear &mdash; room number, vehicle number, or whatever your flow requires. Always identity first.</p>
                <ul class="wizard-bullets">
                  <li>Verified by UIDAI &mdash; not by you</li>
                  <li>Custom fields load from your flow config</li>
                  <li>No identity data stored on your page</li>
                </ul>
                <span class="wizard-tag wizard-tag--blue">UIDAI verified &middot; Custom fields</span>
              </div>
            </div>

            <div class="wizard-step">
              <div class="wizard-step-head">
                <div class="wizard-num" style="background: linear-gradient(135deg, #7c3aed, #9333ea)">03</div>
              </div>
              <div class="wizard-card">
                <div class="wizard-icon" style="background: rgba(124,58,237,0.1); color: #7c3aed">
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="24" height="24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="m9 15 2 2 4-4"/></svg>
                </div>
                <h3>Record delivered to your storage</h3>
                <p>Identity and custom fields merge into one record and are pushed immediately to your webhook or Google Sheet. IDGate does not retain guest data &mdash; once delivered, it lives in your systems.</p>
                <ul class="wizard-bullets">
                  <li>Delivered to your webhook or Google Sheet</li>
                  <li>You own the record &mdash; IDGate does not</li>
                  <li>Guest receives a receipt with scannable QR</li>
                </ul>
                <span class="wizard-tag wizard-tag--purple">Your storage &middot; Your ownership</span>
              </div>
            </div>

          </div>
        </div>
      </section>

      <section class="section" id="data-ownership" aria-labelledby="ownership-heading">
        <div class="wrap">
          <div class="section-head">
            <h2 id="ownership-heading">Your guests&rsquo; data goes to your systems &mdash; not ours.</h2>
            <p class="muted" style="margin-top:0.5rem">As a trusted UIDAI partner, IDGate routes every verification through UIDAI&rsquo;s official infrastructure &mdash; and as a secure data processor, immediately transfers the record to the storage you control. You are the data fiduciary under DPDP.</p>
          </div>

          <div class="data-flow">

            <div class="data-node">
              <div class="data-node-icon" style="background: rgba(15,118,110,0.1); color: #0f766e">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="24" height="24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>
              </div>
              <h3>Processed in memory</h3>
              <p>IDGate merges the UIDAI-verified identity with your custom check-in fields into a single record. No guest data is written to IDGate&rsquo;s database at this step.</p>
            </div>

            <div class="data-arrow" aria-hidden="true">&rarr;</div>

            <div class="data-node">
              <div class="data-node-icon" style="background: rgba(37,99,235,0.1); color: #2563eb">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="24" height="24"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              </div>
              <h3>Pushed to your storage</h3>
              <p>The record is delivered immediately to your webhook endpoint or Google Sheet. Your configuration determines where it lands &mdash; IDGate is the pipe, not the destination.</p>
            </div>

            <div class="data-arrow" aria-hidden="true">&rarr;</div>

            <div class="data-node">
              <div class="data-node-icon" style="background: rgba(124,58,237,0.1); color: #7c3aed">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="24" height="24"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/><path d="M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3"/></svg>
              </div>
              <h3>Owned by you</h3>
              <p>Once delivered, the record lives in your Google Sheet or your systems under your access controls. IDGate retains no copy. Your data, your responsibility.</p>
            </div>

          </div>

          <div class="data-custodian-note">
            <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18" style="flex-shrink:0; margin-top:2px; color: #0f766e"><circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
            <p><strong>Prefer IDGate to hold the records?</strong> We can act as a secure data custodian if you don&rsquo;t configure your own storage endpoint &mdash; but the data remains yours and you remain the responsible fiduciary under DPDP. You can export or delete it from your console at any time.</p>
          </div>
        </div>
      </section>

      <section class="section" id="for-operators" aria-labelledby="operators-heading">
        <div class="wrap">
          <div class="section-head">
            <h2 id="operators-heading">Configure once. Run every check-in from there.</h2>
          </div>
          <div class="feature-grid feature-grid-4">
            <article class="card">
              <div class="card-icon" style="background: linear-gradient(135deg, #2563eb, #4f46e5)">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="22" height="22"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>
              </div>
              <h3>Flow builder</h3>
              <p>Define what each check-in collects. Set the fields your staff need &mdash; room details, vehicle info, stay duration &mdash; and publish a QR. Guests see exactly what you configured, nothing more.</p>
            </article>
            <article class="card">
              <div class="card-icon" style="background: linear-gradient(135deg, #0f766e, #0d9488)">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="22" height="22"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>
              </div>
              <h3>Identity compliance, handled</h3>
              <p>Aadhaar verification happens through UIDAI's official flow. Consent is captured in the Aadhaar app, not on your page. Your team gets the outcome &mdash; verified or not &mdash; without owning the credential process.</p>
            </article>
            <article class="card">
              <div class="card-icon" style="background: linear-gradient(135deg, #0891b2, #38bdf8)">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="22" height="22"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="m9 15 2 2 4-4"/></svg>
              </div>
              <h3>Records where you already work</h3>
              <p>Each completed check-in lands in your Google Sheet or fires your webhook. No new dashboard to monitor, no data migration. It fits into the tools your operations team already uses.</p>
            </article>
            <article class="card">
              <div class="card-icon" style="background: linear-gradient(135deg, #7c3aed, #9333ea)">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="22" height="22"><path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              </div>
              <h3>Role-based access</h3>
              <p>Control who can configure flows, view records, and manage your team. Set it up once, hand it to the right people.</p>
            </article>
          </div>
        </div>
      </section>

      <section class="section alt" id="verticals" aria-labelledby="verticals-heading">
        <div class="wrap">
          <div class="section-head">
            <h2 id="verticals-heading">Built for regulated check-in, across every sector.</h2>
            <p class="muted" style="margin-top:0.5rem">Any business that needs to know who walked in &mdash; and prove it.</p>
          </div>
          <div class="industry-usecases-img">
            <img src="/idgate-usecases.png" alt="IDGate use cases across 15 industries — hospitality, financial services, retail, government, healthcare, co-working, events, education, clubs, IT, PG accommodations, logistics, coaching, insurance, and gated communities" style="width:100%;height:auto;border-radius:1rem;box-shadow:0 4px 24px rgba(0,0,0,0.10)" loading="lazy" />
          </div>
          <div class="industry-grid">

            <a href="/hotels" class="industry-card">
              <div class="industry-icon" style="background: rgba(15,118,110,0.1); color: #0f766e">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="24" height="24"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
              </div>
              <h3>Hospitality</h3>
              <p>Replace paper registers at hotels, resorts, and guesthouses. Aadhaar-verified check-in in under a minute, with records auto-delivered to your PMS.</p>
              <ul class="industry-bullets">
                <li>Hotels, resorts &amp; boutique stays</li>
                <li>Homestays, hostels &amp; PG accommodation</li>
                <li>Dharamshalas &amp; pilgrim lodges</li>
                <li>Form C compliance, paperless</li>
              </ul>
              <span class="industry-card-cta">See how it works &#8250;</span>
            </a>

            <a href="/banking" class="industry-card">
              <div class="industry-icon" style="background: rgba(37,99,235,0.1); color: #2563eb">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="24" height="24"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
              </div>
              <h3>Financial Services</h3>
              <p>Verify walk-in customers for branch KYC, loan applications, and account opening &mdash; with a consent trail that satisfies auditors.</p>
              <ul class="industry-bullets">
                <li>Branch KYC &amp; account opening</li>
                <li>NBFC &amp; microfinance onboarding</li>
                <li>Insurance policy issuance</li>
                <li>Locker access &amp; safe deposit verification</li>
              </ul>
              <span class="industry-card-cta">See how it works &#8250;</span>
            </a>

            <a href="/retail" class="industry-card">
              <div class="industry-icon" style="background: rgba(234,88,12,0.1); color: #ea580c">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="24" height="24"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
              </div>
              <h3>Retail &amp; Delivery</h3>
              <p>Age-gate restricted purchases at the point of sale or delivery. Verification proof stored without retaining the buyer&rsquo;s ID.</p>
              <ul class="industry-bullets">
                <li>Alcohol &amp; tobacco retail</li>
                <li>Pharmacy &amp; prescription fulfilment</li>
                <li>Age-restricted last-mile delivery</li>
                <li>High-value goods pickup verification</li>
              </ul>
              <span class="industry-card-cta">See how it works &#8250;</span>
            </a>

            <a href="/jan-seva-kendra" class="industry-card">
              <div class="industry-icon" style="background: rgba(124,58,237,0.1); color: #7c3aed">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="24" height="24"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="12" y2="14"/></svg>
              </div>
              <h3>Government &amp; Public Services</h3>
              <p>Secure citizen access to service centres, benefit counters, and public venues. Verified identity at the gate, clean audit log in your system.</p>
              <ul class="industry-bullets">
                <li>Jan Seva Kendra &amp; CSC counters</li>
                <li>Subsidy &amp; welfare scheme disbursement</li>
                <li>Court &amp; tribunal visitor registration</li>
                <li>Public infrastructure access control</li>
              </ul>
              <span class="industry-card-cta">See how it works &#8250;</span>
            </a>

            <a href="/healthcare" class="industry-card">
              <div class="industry-icon" style="background: rgba(6,182,212,0.1); color: #0891b2">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="24" height="24"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
              </div>
              <h3>Healthcare &amp; Diagnostics</h3>
              <p>Confirm patient identity at the point of care without handling physical documents. Consent captured before any health data is recorded.</p>
              <ul class="industry-bullets">
                <li>OPD &amp; clinic patient registration</li>
                <li>Diagnostic centre sample collection</li>
                <li>Pharmacy prescription verification</li>
                <li>Ayushman Bharat &amp; insurance claim intake</li>
              </ul>
              <span class="industry-card-cta">See how it works &#8250;</span>
            </a>

            <a href="/co-working" class="industry-card">
              <div class="industry-icon" style="background: rgba(245,158,11,0.1); color: #d97706">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="24" height="24"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/></svg>
              </div>
              <h3>Co-working &amp; Real Estate</h3>
              <p>Know exactly who is on your premises at any time. Replace paper visitor logs with verified digital records your security team can audit instantly.</p>
              <ul class="industry-bullets">
                <li>Co-working space member &amp; visitor entry</li>
                <li>Property site visit registration</li>
                <li>Tenant move-in identity verification</li>
                <li>Corporate office visitor management</li>
              </ul>
              <span class="industry-card-cta">See how it works &#8250;</span>
            </a>

            <a href="/events" class="industry-card">
              <div class="industry-icon" style="background: rgba(236,72,153,0.1); color: #db2777">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="24" height="24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              </div>
              <h3>Events &amp; Entertainment</h3>
              <p>Gate-verify attendees at conferences, concerts, and sporting events. Capture a verified identity trail without a paper form in sight.</p>
              <ul class="industry-bullets">
                <li>Conference &amp; trade show registration</li>
                <li>Concert &amp; sporting event entry</li>
                <li>VIP lounge &amp; backstage access</li>
                <li>Casino &amp; gaming venue age check</li>
              </ul>
              <span class="industry-card-cta">See how it works &#8250;</span>
            </a>

            <a href="/schools" class="industry-card">
              <div class="industry-icon" style="background: rgba(79,70,229,0.1); color: #4f46e5">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="24" height="24"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
              </div>
              <h3>Education &amp; Examination</h3>
              <p>Prevent impersonation at exam halls and campus entry points. A verified record ties each admission to a confirmed Aadhaar identity.</p>
              <ul class="industry-bullets">
                <li>Competitive exam hall admission</li>
                <li>University campus visitor access</li>
                <li>Hostel &amp; mess entry management</li>
                <li>Coaching centre enrolment verification</li>
              </ul>
              <span class="industry-card-cta">See how it works &#8250;</span>
            </a>

            <a href="/gyms" class="industry-card">
              <div class="industry-icon" style="background: rgba(251,146,60,0.12); color: #ea580c">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="24" height="24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
              </div>
              <h3>Clubs &amp; Associations</h3>
              <p>Onboard members and register visitors with a verified identity from day one. No photocopies, no manual entry &mdash; just a clean membership record tied to Aadhaar.</p>
              <ul class="industry-bullets">
                <li>Loyalty &amp; membership enrolment</li>
                <li>Sports club &amp; gym sign-up</li>
                <li>Resident welfare association access</li>
                <li>Professional body &amp; trade association registry</li>
              </ul>
              <span class="industry-card-cta">See how it works &#8250;</span>
            </a>

            <a href="/corporate" class="industry-card">
              <div class="industry-icon" style="background: rgba(15,118,110,0.12); color: #0f766e">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="24" height="24"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8"/><path d="M12 17v4"/></svg>
              </div>
              <h3>IT &amp; Tech Companies</h3>
              <p>Verify contractors, vendors, and visitors before they step inside your campus or data centre. One scan replaces the paper logbook at the gate.</p>
              <ul class="industry-bullets">
                <li>Contractor &amp; vendor onsite check-in</li>
                <li>Data centre visitor access log</li>
                <li>Intern &amp; joining-day identity confirmation</li>
                <li>Multi-campus unified visitor record</li>
              </ul>
              <span class="industry-card-cta">See how it works &#8250;</span>
            </a>

          </div>
        </div>
      </section>

      <section class="section" id="integrate" aria-labelledby="integrate-heading">
        <div class="wrap">
          <div class="section-head">
            <h2 id="integrate-heading">Plugs into your stack without rebuilding it.</h2>
          </div>
          <div class="feature-grid feature-grid-4">
<article class="card">
              <div class="card-icon" style="background: linear-gradient(135deg, #0f766e, #14b8a6)">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="22" height="22"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>
              </div>
              <h3>Webhooks and Google Sheets</h3>
              <p>Completed check-ins deliver to a webhook endpoint you control, or directly into a Google Sheet your team already manages. No middleware required.</p>
            </article>
            <article class="card">
              <div class="card-icon" style="background: linear-gradient(135deg, #1e3a8a, #2563eb)">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="22" height="22"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              </div>
              <h3>Secure by design</h3>
              <p>Verification happens through UIDAI &mdash; IDGate never substitutes for or replicates that process. Your guests verify with the same official Aadhaar flow they know.</p>
            </article>
            <article class="card">
              <div class="card-icon" style="background: linear-gradient(135deg, #db2777, #ea580c)">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="22" height="22"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              </div>
              <h3>Try it yourself</h3>
              <p>Sign up and run the full check-in flow in a sandbox environment. When you're ready to go live against UIDAI's production servers, request an upgrade.</p>
            </article>
          </div>
        </div>
      </section>

      <section class="section" id="cta" aria-labelledby="cta-heading">
        <div class="wrap">
          <div class="cta-panel">
            <div>
              <h2 id="cta-heading">Start your free pilot. Go live when you're ready.</h2>
              <p>Sign up to configure your first flow and test end-to-end in a sandbox. Request an upgrade when you're ready to connect to UIDAI's production servers &mdash; or reach out and we'll walk you through it.</p>
            </div>
            <div class="cta-actions">
              <a class="btn btn-primary" href="https://app.idgate.in/signup" rel="noopener noreferrer">Start free pilot</a>
<a class="btn btn-outline" href="/contact">Contact us</a>
            </div>
          </div>
        </div>
      </section>
    </main>`;
